/**
 * Telegraph visual — the THIN Three.js builder over `telegraphGeometry.ts`:
 * turns a wire `TelegraphShape` into a rim + fill lying in the ground plane,
 * plus the one function the frame loop calls to drive the wind-up fill.
 *
 * - Colour is the Bible §14.1 reserved hostile hue, taken from the ONE place
 *   the client already names it (`RESERVED_COMBAT_HUES[0]`, groundPalette.ts),
 *   so a third value cannot appear (VFX-018 D-1).
 * - `circle` / `point` build EXACTLY the shipped meshes (`RingGeometry(r−0.06,
 *   r, 48)` rim + `CircleGeometry(r, 48)` fill at scale 0.01) — only the hue
 *   moved. Every other kind is the geometry module's polygon as a
 *   `ShapeGeometry` (fill) and outline-minus-inset band (rim); a ring's rim is
 *   two `RingGeometry` bands and its fill grows from the safe hole outward.
 * - Materials match the shipped ones (MeshBasicMaterial, DoubleSide,
 *   transparent; rim α 0.95, fill α 0.30). No lighting: a telegraph reads the
 *   same in a dark dungeon and at noon.
 * - The group is returned UNPLACED; `Game.spawnTelegraph` seats it on the
 *   terrain (AD-408) and tags it transient exactly as before.
 *
 * Pure THREE geometry — no renderer, no DOM — so it also runs under
 * `node --test` (telegraphVisual.test.ts proves the meshes land on the server's
 * hit-test).
 */

import * as THREE from 'three';
import type { TelegraphShape } from '@great-cloud/shared/client';

import { RESERVED_COMBAT_HUES } from '../scene/groundPalette.js';
import {
  circlePolygon,
  describeTelegraph,
  insetPolygon,
  TELEGRAPH_SEGMENTS,
  type TelegraphGeometry,
  type Vec2,
} from './telegraphGeometry.js';

/** The reserved hostile ground-danger hue (Bible §14.1) — the ONLY telegraph colour. */
export const TELEGRAPH_HOSTILE: string = RESERVED_COMBAT_HUES[0];
/** Rim band thickness in metres (the shipped `RingGeometry(radius − 0.06, radius)`). */
export const TELEGRAPH_RIM_WIDTH_M = 0.06;
export const TELEGRAPH_RIM_OPACITY = 0.95;
export const TELEGRAPH_FILL_OPACITY = 0.3;
/** The fill's starting scale — visible as a dot from frame one, exactly as shipped. */
export const TELEGRAPH_FILL_MIN_SCALE = 0.01;

export interface TelegraphVisualParts {
  /** Rim + fill, lying in the ground plane (local +y up), origin at the shape's x/z; unplaced. */
  group: THREE.Group;
  /** The rim mesh (full-size from frame one). */
  rim: THREE.Mesh;
  /** The fill mesh the wind-up drives. */
  fill: THREE.Mesh;
  geometry: TelegraphGeometry;
  /** Drive the wind-up fill: `progress` ∈ [0, 1], 1 = solid at `resolveTick`. */
  setProgress(progress: number): void;
}

function material(opacity: number): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({
    color: TELEGRAPH_HOSTILE,
    transparent: true,
    opacity,
    side: THREE.DoubleSide,
  });
}

/** Lay a mesh built in its local XY plane flat on the ground (the shipped `rotation.x = −π/2`). */
function layFlat(mesh: THREE.Mesh): THREE.Mesh {
  mesh.rotation.x = -Math.PI / 2;
  return mesh;
}

/**
 * Local ground (x, z) → the mesh's XY plane. Under `rotation.x = −π/2` a local
 * (x, y, 0) lands at (x, 0, −y), so ground z is authored as −y.
 */
function toPlane(p: Vec2): THREE.Vector2 {
  return new THREE.Vector2(p.x, -p.z);
}

function shapeGeometry(outer: Vec2[], holes: Vec2[][] = []): THREE.ShapeGeometry {
  const shape = new THREE.Shape(outer.map(toPlane));
  for (const hole of holes) shape.holes.push(new THREE.Path(hole.map(toPlane)));
  return new THREE.ShapeGeometry(shape);
}

/**
 * An annulus whose OUTER edge can be moved without reallocation — the ring's
 * fill grows from the safe hole outward, so the hole never appears to close.
 * Vertex layout: inner ring then outer ring, `segments + 1` each (seam shared).
 */
function annulusFill(
  inner: number,
  outer: number,
  segments: number,
): { geometry: THREE.BufferGeometry; setOuter(radius: number): void } {
  const ringCount = segments + 1;
  const positions = new Float32Array(ringCount * 2 * 3);
  const write = (ring: 0 | 1, radius: number): void => {
    for (let i = 0; i < ringCount; i += 1) {
      const heading = (i / segments) * Math.PI * 2;
      const o = (ring * ringCount + i) * 3;
      positions[o] = Math.sin(heading) * radius;
      positions[o + 1] = -Math.cos(heading) * radius; // ground z authored as −y
      positions[o + 2] = 0;
    }
  };
  write(0, inner);
  write(1, outer);
  const indices: number[] = [];
  for (let i = 0; i < segments; i += 1) {
    const a = i;
    const b = i + 1;
    const c = ringCount + i;
    const d = ringCount + i + 1;
    indices.push(a, c, b, b, c, d);
  }
  const geometry = new THREE.BufferGeometry();
  const attr = new THREE.BufferAttribute(positions, 3);
  geometry.setAttribute('position', attr);
  geometry.setIndex(indices);
  return {
    geometry,
    setOuter(radius: number): void {
      write(1, radius);
      attr.needsUpdate = true;
    },
  };
}

/** Build the rim + fill for a wire telegraph shape. Never throws on a well-typed shape. */
export function buildTelegraphVisual(shape: TelegraphShape): TelegraphVisualParts {
  const geometry = describeTelegraph(shape, TELEGRAPH_SEGMENTS);
  const group = new THREE.Group();
  const outline = geometry.outline;

  if (outline.kind === 'circle') {
    // The shipped circle/point path, byte for byte — only the hue moved (D-1).
    const radius = outline.radius;
    const rim = layFlat(
      new THREE.Mesh(
        new THREE.RingGeometry(radius - TELEGRAPH_RIM_WIDTH_M, radius, TELEGRAPH_SEGMENTS),
        material(TELEGRAPH_RIM_OPACITY),
      ),
    );
    group.add(rim);
    const fill = layFlat(
      new THREE.Mesh(
        new THREE.CircleGeometry(radius, TELEGRAPH_SEGMENTS),
        material(TELEGRAPH_FILL_OPACITY),
      ),
    );
    fill.scale.setScalar(TELEGRAPH_FILL_MIN_SCALE);
    group.add(fill);
    return {
      group,
      rim,
      fill,
      geometry,
      setProgress: (p) => fill.scale.setScalar(clampProgress(p)),
    };
  }

  if (outline.kind === 'annulus') {
    const { innerRadius, outerRadius } = outline;
    const rimGroup = new THREE.Group();
    const rimOuter = layFlat(
      new THREE.Mesh(
        new THREE.RingGeometry(
          Math.max(innerRadius, outerRadius - TELEGRAPH_RIM_WIDTH_M),
          outerRadius,
          TELEGRAPH_SEGMENTS,
        ),
        material(TELEGRAPH_RIM_OPACITY),
      ),
    );
    rimGroup.add(rimOuter);
    if (innerRadius > 0) {
      // The safe hole gets its own edge — "get in" reads as clearly as "get out".
      rimGroup.add(
        layFlat(
          new THREE.Mesh(
            new THREE.RingGeometry(
              innerRadius,
              Math.min(outerRadius, innerRadius + TELEGRAPH_RIM_WIDTH_M),
              TELEGRAPH_SEGMENTS,
            ),
            rimOuter.material as THREE.Material,
          ),
        ),
      );
    }
    group.add(rimGroup);
    const band = annulusFill(innerRadius, innerRadius, TELEGRAPH_SEGMENTS);
    const fill = layFlat(new THREE.Mesh(band.geometry, material(TELEGRAPH_FILL_OPACITY)));
    group.add(fill);
    return {
      group,
      rim: rimOuter,
      fill,
      geometry,
      setProgress: (p) =>
        band.setOuter(innerRadius + (outerRadius - innerRadius) * clampProgress(p)),
    };
  }

  // Sector / rect / cross: the polygon as fill, outline-minus-inset as rim.
  const outer = geometry.polygon.length >= 3 ? geometry.polygon : circlePolygon(0);
  const rim = layFlat(
    new THREE.Mesh(
      shapeGeometry(outer, [insetPolygon(outer, TELEGRAPH_RIM_WIDTH_M)]),
      material(TELEGRAPH_RIM_OPACITY),
    ),
  );
  group.add(rim);
  const fill = layFlat(new THREE.Mesh(shapeGeometry(outer), material(TELEGRAPH_FILL_OPACITY)));
  fill.scale.setScalar(TELEGRAPH_FILL_MIN_SCALE);
  group.add(fill);
  return {
    group,
    rim,
    fill,
    geometry,
    setProgress: (p) => fill.scale.setScalar(clampProgress(p)),
  };
}

/** The shipped clamp: never below the visible dot, never past solid. */
function clampProgress(p: number): number {
  return Math.min(1, Math.max(TELEGRAPH_FILL_MIN_SCALE, Number.isFinite(p) ? p : 0));
}
